export interface PercentEntry {
  id: string;
  percent: number;
}

/** Split between two parties: first gets `splitPercent`, second gets remainder. */
export function twoWaySplit(firstPercent: number): [number, number] {
  const clamped = Math.max(0, Math.min(100, firstPercent));
  return [clamped, 100 - clamped];
}

/**
 * When one slider changes, set that entry to `newValue` and scale others proportionally to sum 100%.
 */
export function renormalizePercents(
  entries: PercentEntry[],
  changedIndex: number,
  newValue: number
): PercentEntry[] {
  if (entries.length === 0) return entries;
  if (entries.length === 1) {
    return [{ ...entries[0], percent: 100 }];
  }
  if (entries.length === 2) {
    const [a, b] = changedIndex === 0 ? twoWaySplit(newValue) : twoWaySplit(100 - newValue);
    return [
      { ...entries[0], percent: a },
      { ...entries[1], percent: b },
    ];
  }

  const clamped = Math.max(0, Math.min(100, newValue));
  const others = entries.filter((_, i) => i !== changedIndex);
  const othersTotal = others.reduce((sum, entry) => sum + entry.percent, 0);
  const remainder = 100 - clamped;

  if (othersTotal <= 0) {
    const even = remainder / others.length;
    return entries.map((entry, index) =>
      index === changedIndex ? { ...entry, percent: clamped } : { ...entry, percent: even }
    );
  }

  return entries.map((entry, index) => {
    if (index === changedIndex) {
      return { ...entry, percent: clamped };
    }
    const scaled = (entry.percent / othersTotal) * remainder;
    return { ...entry, percent: scaled };
  });
}

/** Round percents for display while preserving approximate total. */
export function roundPercents(entries: PercentEntry[]): PercentEntry[] {
  const rounded = entries.map((entry) => ({
    ...entry,
    percent: Math.round(entry.percent),
  }));
  const total = rounded.reduce((sum, entry) => sum + entry.percent, 0);
  if (total === 100 || rounded.length === 0) return rounded;
  const diff = 100 - total;
  const last = rounded[rounded.length - 1];
  if (last) {
    rounded[rounded.length - 1] = { ...last, percent: last.percent + diff };
  }
  return rounded;
}

/** Scale current values proportionally so they sum to 100%. */
export function normalizeToHundred(entries: PercentEntry[]): PercentEntry[] {
  if (entries.length === 0) return entries;
  if (entries.length === 1) {
    return [{ ...entries[0], percent: 100 }];
  }
  const total = entries.reduce((sum, entry) => sum + entry.percent, 0);
  if (total <= 0) {
    const even = 100 / entries.length;
    return entries.map((entry) => ({ ...entry, percent: even }));
  }
  return roundPercents(
    entries.map((entry) => ({
      ...entry,
      percent: (entry.percent / total) * 100,
    }))
  );
}

/** Split 100% evenly across all entries (e.g. 33/33/34 for three parties). */
export function equalSplitPercents(entries: PercentEntry[]): PercentEntry[] {
  if (entries.length === 0) return entries;
  if (entries.length === 1) {
    return [{ ...entries[0], percent: 100 }];
  }
  const even = 100 / entries.length;
  return roundPercents(entries.map((entry) => ({ ...entry, percent: even })));
}
